import { useNavigation } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { LayoutTemplate } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Tabs, useTabsContext, XStack, YGroup } from "tamagui";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import { Body } from "../../../ui/Body";
import { Label } from "../../../ui/Label";
import { FullScreenLoading } from "../../../ui/Loading";
import { BusinessModuleType } from "../../modules/types";
import { BusinessAdminView } from "../BusinessAdminView";
import { BusinessAdminTemplateInfo } from "./BusinessAdminTemplateInfo";
import { BusinessAdminTemplateQuestions } from "./BusinessAdminTemplateQuestions";
import { BusinessAdminTemplateSchedule } from "./BusinessAdminTemplateSchedule";
import { BusinessAdminTemplateSections } from "./BusinessAdminTemplateSections";
import { BusinessAdminTemplateTasks } from "./BusinessAdminTemplateTasks";

type Props = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  "CustomerAdminTemplate"
>;

const Tab: React.FC<{
  label: string;
  value: string;
}> = (props) => {
  const tabsContext = useTabsContext();
  const TextComponent = tabsContext.value === props.value ? Label : Body;

  return (
    <Tabs.Tab
      paddingVertical={24}
      paddingHorizontal={32}
      borderRadius={0}
      focusStyle={{ backgroundColor: "$mutedBackground" }}
      value={props.value}
    >
      <TextComponent>{props.label}</TextComponent>
    </Tabs.Tab>
  );
};

export const BusinessAdminTemplateScreen = observer(({ route }: Props) => {
  const customerUid = route.params.customerUid;
  const templateUid = route.params.templateUid;

  const navigation = useNavigation();
  const { businessTemplatesApi } = useApi();
  const { businessTemplatesStore } = useStores();

  const template = businessTemplatesStore.byId.get(templateUid);

  React.useEffect(() => {
    return businessTemplatesApi.watchItem(templateUid);
  }, [templateUid, businessTemplatesApi]);

  if (!template) {
    return <FullScreenLoading />;
  }

  return (
    <BusinessAdminView customerUid={customerUid}>
      <XStack gap={"$medium"} marginVertical={"$medium"} alignItems={"center"}>
        <LayoutTemplate color={"$muted"} />
        <Label>Template {template.data.planInfo.title}</Label>
      </XStack>

      <Tabs
        defaultValue={route.params?.tab ?? "settings"}
        orientation="horizontal"
        flexDirection="column"
        onValueChange={(value) => {
          navigation.navigate("CustomerAdminTemplate", {
            customerUid: route.params.customerUid,
            templateUid: route.params.templateUid,
            tab: value,
          });
        }}
      >
        <YGroup gap={"$medium"}>
          <Tabs.List disablePassBorderRadius aria-label="Manage your account">
            <Tab value="settings" label="Settings" />
            <Tab value="schedule" label="Schedule" />
            <Tab value="tasks" label="Tasks" />
            <Tab value="sections" label="Info" />
            <Tab value="questions" label="Questions" />
          </Tabs.List>
          <Tabs.Content value="settings">
            <BusinessAdminTemplateInfo template={template} />
          </Tabs.Content>
          <Tabs.Content value="schedule">
            <BusinessAdminTemplateSchedule template={template} />
          </Tabs.Content>
          <Tabs.Content value="tasks">
            <BusinessAdminTemplateTasks
              title={"Tasks"}
              overrideTypes={[BusinessModuleType.Reminder]}
              businessModules={template.data.businessModules}
              onSave={(moduleData) => {
                return template.addBusinessTemplateModule(moduleData);
              }}
              onEdit={(t) => {
                navigation.navigate("CustomerAdminTemplateEdit", {
                  customerUid,
                  templateUid,
                  moduleUid: t.uid,
                });
              }}
              onDelete={(t) => {
                return template.deleteBusinessTemplateModule(t);
              }}
              onOrderChange={(items) => {
                return template.updateBusinessTemplateOrder(items);
              }}
            />
          </Tabs.Content>
          <Tabs.Content value="sections">
            <BusinessAdminTemplateSections template={template} />
          </Tabs.Content>
          <Tabs.Content value="questions">
            <BusinessAdminTemplateQuestions template={template} />
          </Tabs.Content>
        </YGroup>
      </Tabs>
    </BusinessAdminView>
  );
});
